import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight,
  CalendarPlus,
  CheckCircle2,
  ChevronLeft,
  Clock,
  FileText,
  History,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  Plus,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  usePipelineEtapes,
  usePipelineMotifsPerte,
  usePipelineChamps,
  usePipeline,
} from '@/hooks/queries/useCrmPipelines';
import {
  useChampsValues,
  useChangeEtape,
  useHistorique,
  useMarquerPerdue,
  useOpportunite,
  useUpsertChampValue,
} from '@/hooks/queries/useCrmOpportunites';
import { useOppActivites, useUpsertOppActivite } from '@/hooks/queries/useCrmActivites';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { useFactureRecouvrement } from '@/hooks/queries/useRecouvrementEntreprises';
import { useInscriptions } from '@/hooks/queries/useFormation';
import { formatDA, formatDateTimeDZ, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type {
  Json,
  OppActiviteType,
  OpportuniteActiviteRow,
  PipelineChampCustomRow,
} from '@/lib/database.types';
import { OpportuniteFormDialog } from './OpportuniteFormDialog';
import { ConversionModal } from './ConversionModal';

const ACTIVITE_ICON: Record<OppActiviteType, JSX.Element> = {
  note: <FileText className="h-3.5 w-3.5" />,
  appel: <Phone className="h-3.5 w-3.5" />,
  email: <Mail className="h-3.5 w-3.5" />,
  rdv: <CalendarPlus className="h-3.5 w-3.5" />,
  tache: <CheckCircle2 className="h-3.5 w-3.5" />,
  fichier: <FileText className="h-3.5 w-3.5" />,
};

type Tab = 'details' | 'contacts' | 'activites' | 'fichiers' | 'historique' | 'conversion';

export function OpportuniteDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: opp } = useOpportunite(id ?? null);
  const { data: pipeline } = usePipeline(opp?.pipeline_id ?? null);
  const { data: etapes = [] } = usePipelineEtapes(opp?.pipeline_id ?? null);
  const { data: champs = [] } = usePipelineChamps(opp?.pipeline_id ?? null);
  const { data: motifs = [] } = usePipelineMotifsPerte(opp?.pipeline_id ?? null);
  const { data: champsValues = {} } = useChampsValues(id ?? null);
  const { data: activites = [] } = useOppActivites(id ?? null);
  const { data: historique = [] } = useHistorique(id ?? null);
  const { data: tiers = [] } = useTiers({ entrepriseId: opp?.entreprise_id ?? null, search: '' });
  const { data: users = [] } = useUsersList();

  const tier = tiers.find((tt) => tt.id === opp?.tier_id);
  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.full_name ?? u.user_id.slice(0, 8));
    return m;
  }, [users]);

  const changeEtape = useChangeEtape();
  const marquerPerdue = useMarquerPerdue();
  const upsertValue = useUpsertChampValue();
  const upsertAct = useUpsertOppActivite();

  const [tab, setTab] = useState<Tab>('details');
  const [editOpen, setEditOpen] = useState(false);
  const [conversionOpen, setConversionOpen] = useState(false);
  const [perdueDialog, setPerdueDialog] = useState(false);
  const [motifPerteSel, setMotifPerteSel] = useState('');

  const currentEtape = etapes.find((e) => e.id === opp?.etape_id);
  const currentEtapeIndex = etapes.findIndex((e) => e.id === opp?.etape_id);
  const nextEtape = etapes[currentEtapeIndex + 1];
  const lostEtape = etapes.find((e) => e.type === 'lost');

  // Sprint 6.9.19 — Cross-ref vers Recouvrement (facture générée) + Formation (inscriptions liées)
  const factureId =
    (opp as unknown as { facture_generee_id?: string | null } | undefined)?.facture_generee_id ??
    null;
  const { data: facture } = useFactureRecouvrement(factureId);
  const { data: inscData } = useInscriptions({
    opportuniteId: opp?.id ?? null,
    pageSize: 20,
    enabled: !!opp?.id,
  });
  const inscriptionsLiees = inscData?.rows ?? [];

  if (!opp) {
    return (
      <div className="p-6">
        <Link
          to="/crm/liste"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" /> Retour à la liste
        </Link>
        <div className="mt-4 text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  const handleAdvance = async (): Promise<void> => {
    if (!nextEtape) return;
    await changeEtape.mutateAsync({ id: opp.id, etape_id: nextEtape.id });
    toast.success(`Avancée vers « ${nextEtape.nom} »`);
  };

  const isWon = opp.statut === 'won';
  const isLost = opp.statut === 'lost';

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/crm/liste"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-3 w-3" /> Retour à la liste
      </Link>
      <PageHeader
        title={opp.titre}
        subtitle={
          <span className="text-xs text-muted-foreground">
            <span className="font-mono">{opp.reference}</span>
            {pipeline && ` · ${pipeline.nom}`}
            {tier && ` · ${tier.raison_sociale}`}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" /> Modifier
            </Button>
            {!isWon && !isLost && nextEtape && (
              <Button size="sm" onClick={handleAdvance}>
                Faire avancer <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
            {!isWon && !isLost && lostEtape && (
              <Button variant="destructive" size="sm" onClick={() => setPerdueDialog(true)}>
                <XCircle className="h-3.5 w-3.5" /> Perdue
              </Button>
            )}
            {isWon && !opp.echeance_generee_id && (
              <Button size="sm" onClick={() => setConversionOpen(true)}>
                Convertir →
              </Button>
            )}
          </div>
        }
      />

      {/* Stepper visuel */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="scrollbar-thin overflow-x-auto">
          <div className="flex min-w-max items-center gap-2">
            {etapes.map((e, idx) => {
              const isCurrent = e.id === opp.etape_id;
              const isPassed = idx < currentEtapeIndex;
              return (
                <div key={e.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (e.id !== opp.etape_id) {
                        void changeEtape
                          .mutateAsync({ id: opp.id, etape_id: e.id })
                          .then(() => toast.success(`Déplacée vers « ${e.nom} »`));
                      }
                    }}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition',
                      isCurrent && 'border-primary bg-primary/10 font-semibold text-primary',
                      isPassed && 'border-success/40 bg-success/10 text-success',
                      !isCurrent && !isPassed && 'border-border text-muted-foreground',
                    )}
                  >
                    <span>{e.icone_emoji ?? '•'}</span>
                    <span>{e.nom}</span>
                  </button>
                  {idx < etapes.length - 1 && (
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
        {/* Tabs principales */}
        <div className="space-y-4">
          <nav className="flex flex-wrap items-center gap-1 border-b border-border">
            {(
              [
                ['details', t('crm.tabs.details')],
                ['contacts', t('crm.tabs.contacts')],
                ['activites', t('crm.tabs.activites')],
                ['fichiers', t('crm.tabs.fichiers')],
                ['historique', t('crm.tabs.historique')],
                ['conversion', t('crm.tabs.conversion')],
              ] as [Tab, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={cn(
                  'border-b-2 px-3 py-2 text-xs transition-colors',
                  tab === key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </nav>

          {tab === 'details' && (
            <ChampsCustomEditor
              champs={champs}
              values={champsValues}
              onChange={(c, v) => {
                void upsertValue.mutateAsync({
                  opportunite_id: opp.id,
                  champ_id: c.id,
                  valeur: v as Json,
                });
              }}
            />
          )}

          {tab === 'activites' && (
            <ActivitesTimeline
              activites={activites}
              users={userMap}
              onAdd={async (type, sujet, description, datePrevue) => {
                await upsertAct.mutateAsync({
                  opportunite_id: opp.id,
                  type,
                  sujet,
                  description,
                  date_prevue: datePrevue,
                  statut: 'a_faire',
                });
                toast.success('Activité ajoutée');
              }}
              onComplete={async (actId) => {
                await upsertAct.mutateAsync({
                  id: actId,
                  opportunite_id: opp.id,
                  type: 'note',
                  statut: 'termine',
                  date_realisee: new Date().toISOString(),
                });
              }}
            />
          )}

          {tab === 'historique' && (
            <section className="rounded-lg border border-border bg-card p-4">
              <header className="mb-3 flex items-center gap-2">
                <History className="h-4 w-4" />
                <h3 className="text-sm font-semibold">Historique des modifications</h3>
              </header>
              {historique.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucune modification enregistrée.</p>
              ) : (
                <ul className="scrollbar-thin max-h-96 space-y-2 overflow-y-auto">
                  {historique.map((h) => (
                    <li key={h.id} className="border-l-2 border-border pl-3 text-xs">
                      <div className="font-medium">{h.action}</div>
                      <div className="text-muted-foreground">
                        {formatDateTimeDZ(h.date)} ·{' '}
                        {h.user_id ? (userMap.get(h.user_id) ?? h.user_id.slice(0, 8)) : 'Système'}
                      </div>
                      {h.champ_modifie && (
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {h.champ_modifie}: {JSON.stringify(h.ancienne_valeur)} →{' '}
                          {JSON.stringify(h.nouvelle_valeur)}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {tab === 'contacts' && (
            <section className="rounded-lg border border-border bg-card p-4 text-xs text-muted-foreground">
              Multi-contacts — gestion enrichie disponible Sprint 5 (pour l&apos;instant : tier
              principal {tier ? `(${tier.raison_sociale})` : '—'}, contacts secondaires à ajouter
              via Paramètres &gt; Tiers).
            </section>
          )}

          {tab === 'fichiers' && (
            <section className="rounded-lg border border-border bg-card p-4 text-xs text-muted-foreground">
              Upload de fichiers via les activités de type « fichier » dans l&apos;onglet Activités.
            </section>
          )}

          {tab === 'conversion' && (
            <section className="space-y-3 rounded-lg border border-border bg-card p-4">
              <h3 className="text-sm font-semibold">Conversion vers les autres modules</h3>
              {!isWon ? (
                <p className="text-xs text-muted-foreground">
                  La conversion est disponible une fois l&apos;opportunité gagnée.
                </p>
              ) : opp.echeance_generee_id ? (
                <p className="text-xs text-success">
                  ✅ Cette opportunité a déjà été convertie. Échéance générée :{' '}
                  <Link to={`/echeances/a-encaisser`} className="text-primary hover:underline">
                    voir échéance
                  </Link>
                </p>
              ) : (
                <Button onClick={() => setConversionOpen(true)}>
                  <Plus className="h-3.5 w-3.5" /> Convertir en facture / échéance
                </Button>
              )}
            </section>
          )}
        </div>

        {/* Side panel */}
        <aside className="space-y-3">
          <div className="rounded-lg border border-border bg-card p-3 text-xs">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-semibold">Statut</span>
              <Badge variant={isWon ? 'success' : isLost ? 'destructive' : 'outline'}>
                {t(`crm.statut.${opp.statut}`)}
              </Badge>
            </div>
            <div className="space-y-1">
              <Row label="Étape" value={currentEtape?.nom ?? '—'} />
              <Row label="Probabilité" value={`${opp.probabilite} %`} />
              <Row label="Montant" value={formatDA(Number(opp.montant_estime_dzd))} />
              <Row
                label="Commercial"
                value={opp.commercial_id ? (userMap.get(opp.commercial_id) ?? '—') : '—'}
              />
              <Row label="Source" value={opp.source ?? '—'} />
              <Row label="Créée le" value={formatDateDZ(opp.date_creation)} />
              <Row
                label="Clôture prévue"
                value={opp.date_cloture_prevue ? formatDateDZ(opp.date_cloture_prevue) : '—'}
              />
              <Row
                label="Dernière activité"
                value={opp.derniere_activite_at ? formatDateTimeDZ(opp.derniere_activite_at) : '—'}
              />
            </div>
          </div>

          {(facture || inscriptionsLiees.length > 0) && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 text-xs">
              <div className="mb-2 font-semibold text-emerald-900">Conversion</div>
              {facture && (
                <Link
                  to="/finances/recouvrement-entreprises"
                  className="mb-2 block rounded-md border border-emerald-200 bg-white px-2 py-1.5 hover:bg-emerald-50/50"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-emerald-700">
                      {facture.reference_facture}
                    </span>
                    <Badge
                      variant={
                        facture.statut === 'regle'
                          ? 'success'
                          : facture.statut === 'partiel'
                            ? 'secondary'
                            : 'outline'
                      }
                      className="text-[9px]"
                    >
                      {facture.statut}
                    </Badge>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-muted-foreground">Facture Recouvrement</span>
                    <span className="num font-semibold">{formatDA(facture.montant_ttc)}</span>
                  </div>
                  {facture.montant_encaisse > 0 && facture.statut !== 'regle' && (
                    <div className="mt-0.5 text-[10px] text-emerald-700">
                      Encaissé : {formatDA(facture.montant_encaisse)} · Reste{' '}
                      {formatDA(facture.montant_ttc - facture.montant_encaisse)}
                    </div>
                  )}
                </Link>
              )}
              {inscriptionsLiees.length > 0 && (
                <div>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                    Inscriptions formation ({inscriptionsLiees.length})
                  </div>
                  <ul className="space-y-1">
                    {inscriptionsLiees.slice(0, 5).map((i) => (
                      <li key={i.id}>
                        <Link
                          to={`/formation/sessions/${i.session_id}`}
                          className="flex items-center justify-between gap-2 rounded-md border border-indigo-100 bg-white px-2 py-1 hover:bg-indigo-50/50"
                        >
                          <span className="font-mono text-[10px] text-indigo-700">
                            {i.reference}
                          </span>
                          <Badge variant="outline" className="text-[9px]">
                            {i.statut}
                          </Badge>
                          <span className="num text-[10px] font-semibold">
                            {formatDA(Number(i.prix_facture))}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="rounded-lg border border-border bg-card p-3 text-xs">
            <div className="mb-2 font-semibold">Prochaines activités</div>
            {activites.filter((a) => a.statut === 'a_faire').length === 0 ? (
              <p className="text-muted-foreground">Aucune.</p>
            ) : (
              <ul className="space-y-1.5">
                {activites
                  .filter((a) => a.statut === 'a_faire')
                  .slice(0, 5)
                  .map((a) => (
                    <li key={a.id} className="flex items-start gap-2">
                      <span className="mt-0.5">{ACTIVITE_ICON[a.type]}</span>
                      <div className="min-w-0 flex-1">
                        <div className="line-clamp-1">{a.sujet ?? a.type}</div>
                        {a.date_prevue && (
                          <div className="text-[10px] text-muted-foreground">
                            <Clock className="mr-1 inline h-2.5 w-2.5" />
                            {formatDateTimeDZ(a.date_prevue)}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </aside>
      </div>

      <OpportuniteFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        entrepriseId={opp.entreprise_id}
        editing={opp}
        onSaved={() => setEditOpen(false)}
      />

      <ConfirmDialog
        open={perdueDialog}
        onOpenChange={(o) => !o && setPerdueDialog(false)}
        destructive
        title="Marquer cette opportunité comme perdue ?"
        description={
          motifs.length === 0
            ? 'Aucun motif de perte configuré pour ce pipeline. Ajoutez-en via Paramètres > CRM.'
            : 'Sélectionnez un motif de perte avant de confirmer.'
        }
        loading={marquerPerdue.isPending}
        onConfirm={async () => {
          if (!motifPerteSel) {
            toast.error('Choisissez un motif de perte');
            return;
          }
          if (!lostEtape) {
            toast.error('Aucune étape de type "lost" dans ce pipeline');
            return;
          }
          await marquerPerdue.mutateAsync({
            id: opp.id,
            motif_perte_id: motifPerteSel,
            etape_lost_id: lostEtape.id,
          });
          toast.success('Opportunité marquée perdue');
          setPerdueDialog(false);
          setMotifPerteSel('');
        }}
      >
        {motifs.length > 0 && (
          <select
            value={motifPerteSel}
            onChange={(e) => setMotifPerteSel(e.target.value)}
            className="mt-3 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">— Sélectionner un motif —</option>
            {motifs.map((m) => (
              <option key={m.id} value={m.id}>
                {m.libelle}
              </option>
            ))}
          </select>
        )}
      </ConfirmDialog>

      <ConversionModal open={conversionOpen} onOpenChange={setConversionOpen} opportunite={opp} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="line-clamp-1 text-right">{value}</span>
    </div>
  );
}

function ChampsCustomEditor({
  champs,
  values,
  onChange,
}: {
  champs: PipelineChampCustomRow[];
  values: Record<string, Json>;
  onChange: (champ: PipelineChampCustomRow, valeur: unknown) => void;
}): JSX.Element {
  const sections = useMemo(() => {
    const groups = new Map<string, PipelineChampCustomRow[]>();
    for (const c of champs) {
      const key = c.section ?? 'Autres';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }
    return Array.from(groups.entries());
  }, [champs]);

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-4">
      <h3 className="text-sm font-semibold">Champs custom du pipeline</h3>
      {champs.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucun champ custom configuré.</p>
      ) : (
        sections.map(([section, chs]) => (
          <div key={section} className="space-y-2">
            <h4 className="text-[10px] uppercase tracking-wide text-muted-foreground">{section}</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {chs.map((c) => (
                <ChampInput
                  key={c.id}
                  champ={c}
                  value={values[c.id] ?? null}
                  onChange={(v) => onChange(c, v)}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </section>
  );
}

function ChampInput({
  champ,
  value,
  onChange,
}: {
  champ: PipelineChampCustomRow;
  value: Json | null;
  onChange: (v: unknown) => void;
}): JSX.Element {
  const v = value as unknown;
  const id = `champ-${champ.id}`;
  const baseInput =
    'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm';

  if (champ.type === 'textarea')
    return (
      <div className="sm:col-span-2">
        <label htmlFor={id} className="text-xs text-muted-foreground">
          {champ.label}
        </label>
        <textarea
          id={id}
          rows={3}
          defaultValue={(v as string) ?? ''}
          onBlur={(e) => onChange(e.target.value)}
          className={cn(baseInput, 'min-h-[80px]')}
        />
      </div>
    );

  if (champ.type === 'boolean')
    return (
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)} />
        {champ.label}
      </label>
    );

  if (champ.type === 'select') {
    const options = (champ.options as string[] | null) ?? [];
    return (
      <div>
        <label htmlFor={id} className="text-xs text-muted-foreground">
          {champ.label}
        </label>
        <select
          id={id}
          value={(v as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={baseInput}
        >
          <option value="">— Choisir —</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (champ.type === 'multiselect') {
    const options = (champ.options as string[] | null) ?? [];
    const arr = (v as string[] | null) ?? [];
    return (
      <div>
        <label className="text-xs text-muted-foreground">{champ.label}</label>
        <div className="flex flex-wrap gap-1">
          {options.map((o) => {
            const active = arr.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => onChange(active ? arr.filter((x) => x !== o) : [...arr, o])}
                className={cn(
                  'rounded-md border px-2 py-0.5 text-[11px]',
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-secondary',
                )}
              >
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor={id} className="text-xs text-muted-foreground">
        {champ.label}
        {champ.obligatoire && <span className="text-destructive"> *</span>}
      </label>
      <input
        id={id}
        type={
          champ.type === 'number'
            ? 'number'
            : champ.type === 'date'
              ? 'date'
              : champ.type === 'datetime'
                ? 'datetime-local'
                : champ.type === 'url'
                  ? 'url'
                  : 'text'
        }
        defaultValue={(v as string | number | null) ?? ''}
        onBlur={(e) =>
          onChange(
            champ.type === 'number'
              ? e.target.value === ''
                ? null
                : Number(e.target.value)
              : e.target.value,
          )
        }
        className={baseInput}
      />
    </div>
  );
}

function ActivitesTimeline({
  activites,
  users,
  onAdd,
  onComplete,
}: {
  activites: OpportuniteActiviteRow[];
  users: Map<string, string>;
  onAdd: (
    type: OppActiviteType,
    sujet: string | null,
    description: string | null,
    datePrevue: string | null,
  ) => Promise<void>;
  onComplete: (actId: string) => Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<OppActiviteType>('note');
  const [sujet, setSujet] = useState('');
  const [description, setDescription] = useState('');
  const [datePrevue, setDatePrevue] = useState('');

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Activités</h3>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)}>
          <Plus className="h-3.5 w-3.5" />
          {t('crm.actions.ajouterActivite')}
        </Button>
      </header>

      {open && (
        <div className="grid grid-cols-1 gap-2 rounded-md border border-border/60 bg-muted/30 p-3 sm:grid-cols-4">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as OppActiviteType)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {(['note', 'appel', 'email', 'rdv', 'tache'] as OppActiviteType[]).map((t2) => (
              <option key={t2} value={t2}>
                {t(`crm.activite.type.${t2}`)}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Sujet"
            value={sujet}
            onChange={(e) => setSujet(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          />
          <input
            type="datetime-local"
            value={datePrevue}
            onChange={(e) => setDatePrevue(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          />
          <input
            type="text"
            placeholder="Description courte"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          />
          <div className="sm:col-span-4">
            <Button
              size="sm"
              onClick={async () => {
                await onAdd(
                  type,
                  sujet || null,
                  description || null,
                  datePrevue ? new Date(datePrevue).toISOString() : null,
                );
                setSujet('');
                setDescription('');
                setDatePrevue('');
                setOpen(false);
              }}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {activites.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucune activité enregistrée.</p>
      ) : (
        <ul className="space-y-2">
          {activites.map((a) => (
            <li
              key={a.id}
              className="flex items-start gap-2 rounded-md border border-border/40 p-2 text-xs"
            >
              <span className="mt-0.5">{ACTIVITE_ICON[a.type]}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{t(`crm.activite.type.${a.type}`)}</Badge>
                  <span className="font-medium">{a.sujet ?? '—'}</span>
                  <Badge
                    variant={
                      a.statut === 'termine'
                        ? 'success'
                        : a.statut === 'a_faire'
                          ? 'outline'
                          : 'secondary'
                    }
                  >
                    {t(`crm.activite.statut.${a.statut}`)}
                  </Badge>
                </div>
                {a.description && <div className="text-muted-foreground">{a.description}</div>}
                <div className="text-[10px] text-muted-foreground">
                  {a.date_prevue && (
                    <>
                      <Clock className="mr-1 inline h-2.5 w-2.5" />
                      {formatDateTimeDZ(a.date_prevue)} ·{' '}
                    </>
                  )}
                  {a.assignee_id ? users.get(a.assignee_id) : 'Non assignée'}
                </div>
              </div>
              {a.statut === 'a_faire' && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => void onComplete(a.id)}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Marquer fait
                </Button>
              )}
              {(a.type === 'email' || a.type === 'note') && (
                <span className="hidden">
                  <MessageSquare className="h-3 w-3" />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
